import { useState, useEffect, useCallback, useRef } from "react";
import { themeGet } from "@styled-system/theme-get";
import { useTheme } from "styled-components";
import { sum } from "lodash";

import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import useWindowResizeListener from "src/hooks/useWindowResizeListener";

const HEADER_SELECTORS = {
  LEGACY: ".guided-tour-team-details-welcome",
  NOVA: "#team-profile-header-container",
  APP_LAYOUT_HEADER: "#app-layout-header",
};

const DEFAULT_SPACING_HEIGHT = 16;
const DEFAULT_FALLBACK_HEIGHT = 64;
const HEADER_POLL_INTERVAL_MS = 100;

const getHeaderElement = () => {
  return document.querySelector(HEADER_SELECTORS.NOVA);
};

/**
 * Utility function to determine spacing multiplier based on profile headers
 * @param {boolean} hasProfileHeaders - Whether profile headers are included
 * @returns {number} The spacing multiplier value
 */
const getSpacingMultiplier = (hasProfileHeaders) => {
  if (hasProfileHeaders) {
    return 3;
  }
  return 2;
};

/**
 * Utility function to convert CSS values (like "16px") to numbers
 * @param {string} value - CSS value string (e.g., "16px", "24px")
 * @param {number} defaultValue - Default value to return if conversion fails
 * @returns {number} The numeric value or defaultValue if conversion fails
 */
const castToNumber = (value, defaultValue) => {
  // Remove all non-numeric characters except decimal points and convert to number
  return parseFloat(value.replace(/[^0-9.]/g, "")) || defaultValue;
};

/**
 * Custom hook that calculates the total height of app headers
 * including navbar, spacing, and profile header (if present)
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeProfileHeaders - Whether to include legacy/nova profile header heights (default: true)
 * @param {boolean} options.hideHeader - Whether the app layout header is hidden (e.g., in embed mode) (default: false)
 * @returns {number} The calculated total height in pixels
 */
export const useAppHeadersHeight = (options = {}) => {
  const { includeProfileHeaders = true, hideHeader = false } = options;
  const [profileHeaderHeight, setProfileHeaderHeight] = useState(0);
  const [appLayoutHeaderHeight, setAppLayoutHeaderHeight] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const featureFlags = useWorkspaceFeatureFlags();
  const theme = useTheme();
  const observerRef = useRef(null);
  const headerElementRef = useRef(null);

  const updateProfileHeaderHeight = useCallback(() => {
    if (!includeProfileHeaders) {
      setProfileHeaderHeight(0);
      return;
    }

    const profileHeaderContainer = getHeaderElement();

    headerElementRef.current = profileHeaderContainer;

    if (profileHeaderContainer) {
      const rect = profileHeaderContainer.getBoundingClientRect();
      setProfileHeaderHeight(rect.height);
    } else {
      setProfileHeaderHeight(0);
    }
  }, [includeProfileHeaders]);

  const updateAppLayoutHeaderHeight = useCallback(() => {
    if (hideHeader) {
      setAppLayoutHeaderHeight(0);
      setIsInitialized(true);
      return;
    }

    const appLayoutHeaderContainer = document.querySelector(
      HEADER_SELECTORS.APP_LAYOUT_HEADER
    );

    if (appLayoutHeaderContainer) {
      const rect = appLayoutHeaderContainer.getBoundingClientRect();
      setAppLayoutHeaderHeight(rect.height);
      setIsInitialized(true);
    } else {
      setAppLayoutHeaderHeight(0);
    }
  }, [hideHeader]);

  const updateCalculatedHeights = useCallback(() => {
    updateProfileHeaderHeight();
    updateAppLayoutHeaderHeight();
  }, [updateProfileHeaderHeight, updateAppLayoutHeaderHeight]);

  useEffect(() => {
    // Poll for header element availability before initial calculation
    let pollForHeadersId = null;

    function pollForHeaders() {
      const appLayoutHeaderContainer = hideHeader
        ? null
        : document.querySelector(HEADER_SELECTORS.APP_LAYOUT_HEADER);
      const profileHeaderContainer = includeProfileHeaders
        ? getHeaderElement()
        : null;

      // do we have all we need to calc header height?
      if (
        (hideHeader || appLayoutHeaderContainer) &&
        (!includeProfileHeaders || profileHeaderContainer)
      ) {
        // calc and stop polling
        updateCalculatedHeights();
      } else {
        // continue polling
        pollForHeadersId = setTimeout(pollForHeaders, HEADER_POLL_INTERVAL_MS);
      }
    }

    pollForHeaders();

    // Always observe app layout header for changes
    const appLayoutHeaderContainer = document.querySelector(
      HEADER_SELECTORS.APP_LAYOUT_HEADER
    );

    // Clean up previous observer before creating a new one
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create a new observer for app layout header
    observerRef.current = new window.ResizeObserver(() => {
      updateCalculatedHeights();
    });

    // Always observe app layout header (unless hidden)
    if (!hideHeader && appLayoutHeaderContainer) {
      observerRef.current.observe(appLayoutHeaderContainer);
    }

    // Only observe profile headers if we're including them
    if (includeProfileHeaders) {
      const profileHeaderContainer = getHeaderElement();
      headerElementRef.current = profileHeaderContainer;

      if (profileHeaderContainer) {
        observerRef.current.observe(profileHeaderContainer);
      }
    }

    return () => {
      if (pollForHeadersId) {
        // in practice there isn't ever actually a queued timeout to clear, but leaving this as safeguard
        clearTimeout(pollForHeadersId);
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [
    featureFlags,
    updateCalculatedHeights,
    includeProfileHeaders,
    hideHeader,
  ]);

  // Use updateCalculatedHeights for window resize events
  useWindowResizeListener(updateCalculatedHeights, false, null);

  // Calculate the total height using theme values with fallbacks
  const spacingHeightStr =
    themeGet("space.r")({ theme }) || `${DEFAULT_SPACING_HEIGHT}px`;

  // Convert theme values to numbers (remove 'px' and convert to number)
  const spacingHeight = castToNumber(spacingHeightStr, DEFAULT_SPACING_HEIGHT);

  // Calculate spacing multiplier based on whether we're including profile headers and app header
  const spacingMultiplier = getSpacingMultiplier(
    includeProfileHeaders || hideHeader // Include spacing if either profile headers or app header is hidden
  );

  const totalHeight = sum([
    spacingHeight * spacingMultiplier,
    profileHeaderHeight,
    appLayoutHeaderHeight,
  ]);

  // Consolidated decision tree for returning header height or fallback
  if (
    (!isInitialized && totalHeight === 0) || // Not initialized and no measurement
    (!hideHeader && appLayoutHeaderHeight <= 0 && profileHeaderHeight <= 0) // No headers found (when header should be visible)
  ) {
    return DEFAULT_FALLBACK_HEIGHT;
  }

  return totalHeight;
};

/**
 * Custom hook that returns the CSS calc expression for 100vh minus app headers height
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeProfileHeaders - Whether to include legacy/nova profile header heights (default: true)
 * @param {boolean} options.hideHeader - Whether the app layout header is hidden (e.g., in embed mode) (default: false)
 * @returns {string} CSS calc expression: calc(100vh - AppHeadersHeight)
 */
export const useAppHeadersHeightCalc = (options = {}) => {
  const appHeadersHeight = useAppHeadersHeight(options);

  // Ensure we always have a valid height, even before headers are loaded
  const safeHeight = appHeadersHeight || DEFAULT_FALLBACK_HEIGHT; // Default to 64px if not calculated yet
  const calcExpression = `calc(100vh - ${safeHeight}px)`;

  return calcExpression;
};
